import React, { useMemo } from "react";
import { W } from "./constants";

// Land masses with LOD (tiny islands only when zoomed in) + hover highlight/tooltip wiring.
export function CountryLayer({ worldPaths, view, toViewCoords, setHoverInfo }) {
  const zoom = W / view.w;
  const threshold = zoom >= 2 ? 0 : (zoom >= 1.3 ? 0.0004 : 0.0015);
  const visiblePaths = useMemo(() => worldPaths.filter((p) => p.area >= threshold), [worldPaths, threshold]);
  return (
    <>
      <g stroke="#C8A96E" strokeOpacity="0.7" strokeWidth="0.5" strokeLinejoin="round">
        {visiblePaths.map((p) => (
          <path
            key={`c-${p.name}-${p.d.length}`}
            d={p.d}
            fill="url(#landFill)"
            vectorEffect="non-scaling-stroke"
            style={{ transition: "fill 0.15s" }}
            onMouseEnter={(e) => {
              e.currentTarget.setAttribute("fill", "#7a2fd0");
              const pt = toViewCoords(e);
              if (pt) setHoverInfo({ name: p.name, capital: p.capital, x: pt.x, y: pt.y });
            }}
            onMouseMove={(e) => {
              const pt = toViewCoords(e);
              if (pt) setHoverInfo((prev) => prev ? { ...prev, x: pt.x, y: pt.y } : prev);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.setAttribute("fill", "url(#landFill)");
              setHoverInfo(null);
            }}
            data-testid={`country-${p.name}`}
          />
        ))}
      </g>
      <g fill="url(#landShade)" stroke="none" pointerEvents="none">
        {worldPaths.map((p) => <path key={`sh-${p.name}-${p.d.length}`} d={p.d} />)}
      </g>
    </>
  );
}

export function ZoomLabels({ zoomK, view, culledCountryLabels, culledCityLabels }) {
  if (zoomK < 2) return null;
  return (
    <g pointerEvents="none" data-testid="map-zoom-labels">
      {culledCountryLabels.map((p) => (
        <text
          key={`cn-${p.name}`}
          x={p.cx}
          y={p.cy}
          fill="#F5E9C4"
          fillOpacity="0.85"
          fontSize={Math.max(6, 9 * (view.w / W))}
          fontFamily="'Cormorant Garamond', serif"
          textAnchor="middle"
          style={{ paintOrder: "stroke", stroke: "#15111A", strokeWidth: 2, strokeOpacity: 0.6 }}
        >
          {p.name}
        </text>
      ))}
      {culledCityLabels.map((c) => (
        <g key={`ct-${c.name}`}>
          <circle cx={c.x} cy={c.y} r={Math.max(0.8, 1.4 * (view.w / W))} fill="#C8A96E" />
          <text
            x={c.x + 3}
            y={c.y - 3}
            fill="#C8A96E"
            fillOpacity="0.95"
            fontSize={Math.max(5, 7 * (view.w / W))}
            fontFamily="'Courier Prime', monospace"
            style={{ textTransform: "uppercase", letterSpacing: "0.5px", paintOrder: "stroke", stroke: "#15111A", strokeWidth: 2, strokeOpacity: 0.7 }}
          >
            {c.name}
          </text>
        </g>
      ))}
    </g>
  );
}

export function CountryTooltip({ hoverInfo, view }) {
  if (!hoverInfo) return null;
  return (
    <div
      data-testid="country-tooltip"
      className="absolute pointer-events-none px-3 py-2 rounded-md bg-[#15111A]/95 border border-[#C8A96E]/40 backdrop-blur-md shadow-lg hidden sm:block"
      style={{
        left: `${((hoverInfo.x - view.x) / view.w) * 100}%`,
        top: `${((hoverInfo.y - view.y) / view.h) * 100}%`,
        transform: "translate(12px, -110%)",
        zIndex: 20,
      }}
    >
      <div className="font-heading text-sm text-white leading-none">{hoverInfo.name}</div>
      {hoverInfo.capital && (
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#C8A96E] mt-1 font-mono-scanner">
          ★ {hoverInfo.capital}
        </div>
      )}
    </div>
  );
}
